import { useState, useEffect, useCallback } from 'react';
import { api } from '@/App';
import { toast } from 'sonner';
import { useTheme } from '@/contexts/ThemeContext';
import {
  Shield, Link2, AlertTriangle, Target, Ticket, TrendingUp, RefreshCw,
  Plus, Settings, CheckCircle, Clock, XCircle, ChevronRight, Zap,
  Activity, BarChart3, PieChart, Users, Calendar, ExternalLink,
  Filter, Search, Play, Loader2, Trash2, Edit, Eye
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { 
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, 
  PieChart as RePieChart, Pie, Cell, LineChart, Line 
} from 'recharts';

const COLORS = {
  critical: '#ef4444',
  high: '#f97316',
  medium: '#eab308',
  low: '#22c55e'
};

export default function CorrelationEngine() {
  const { isDarkMode } = useTheme();
  const [activeTab, setActiveTab] = useState('dashboard');
  const [loading, setLoading] = useState(true);
  const [dashboard, setDashboard] = useState(null);
  const [tickets, setTickets] = useState([]);
  const [ticketStats, setTicketStats] = useState(null);
  const [policyConfig, setPolicyConfig] = useState(null);
  
  // Filters
  const [ticketStatusFilter, setTicketStatusFilter] = useState('all');
  const [ticketPriorityFilter, setTicketPriorityFilter] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  
  // Dialogs
  const [showCreateTicket, setShowCreateTicket] = useState(false);
  const [showPolicySettings, setShowPolicySettings] = useState(false);
  const [showTicketDetail, setShowTicketDetail] = useState(null);
  const [calculating, setCalculating] = useState(false);
  
  // Create ticket form
  const [newTicket, setNewTicket] = useState({
    title: '',
    description: '',
    priority: 'medium',
    assigned_team: '',
    sync_to_external: false
  });

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    fetchDashboard();
    fetchTickets();
    fetchPolicyConfig();
  }, []);

  const fetchDashboard = async () => {
    try {
      const response = await api.get('/correlation/dashboard');
      setDashboard(response.data);
    } catch (error) {
      console.error('Failed to fetch correlation dashboard:', error);
    }
  };

  const fetchTickets = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (ticketStatusFilter !== 'all') params.append('status', ticketStatusFilter);
      if (ticketPriorityFilter !== 'all') params.append('priority', ticketPriorityFilter);
      
      const response = await api.get(`/correlation/tickets?${params}`);
      setTickets(response.data.tickets || []);
      setTicketStats(response.data.stats);
    } catch (error) {
      console.error('Failed to fetch tickets:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchPolicyConfig = async () => {
    try {
      const response = await api.get('/correlation/policy-config');
      setPolicyConfig(response.data);
    } catch (error) {
      console.error('Failed to fetch policy config:', error);
    }
  };

  const handleBulkCalculateRisk = async () => {
    try {
      setCalculating(true);
      const response = await api.post('/correlation/bulk-calculate-risk?recalculate_all=true');
      toast.success(`Processed ${response.data.processed} vulnerabilities`);
      if (response.data.tickets_created?.length > 0) {
        toast.info(`${response.data.tickets_created.length} tickets auto-created by policy`);
      }
      fetchDashboard();
      fetchTickets();
    } catch (error) {
      toast.error('Failed to calculate risk scores');
    } finally {
      setCalculating(false);
    }
  };

  const handleCreateTicket = async () => {
    try {
      if (!newTicket.title || !newTicket.description) {
        toast.error('Title and description are required');
        return;
      }
      
      const response = await api.post('/correlation/tickets', newTicket);
      toast.success('Ticket created successfully');
      setShowCreateTicket(false);
      setNewTicket({ title: '', description: '', priority: 'medium', assigned_team: '', sync_to_external: false });
      fetchTickets();
      fetchDashboard();
    } catch (error) {
      toast.error('Failed to create ticket');
    }
  };

  const handleUpdateTicketStatus = async (ticketId, status) => {
    try {
      await api.put(`/correlation/tickets/${ticketId}`, { status });
      toast.success(`Ticket ${status}`);
      fetchTickets();
      fetchDashboard();
    } catch (error) {
      toast.error('Failed to update ticket');
    }
  };

  const handleDeleteTicket = async (ticketId) => {
    try {
      await api.delete(`/correlation/tickets/${ticketId}`);
      toast.success('Ticket deleted');
      fetchTickets();
      fetchDashboard();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete ticket');
    }
  };

  const handleSavePolicyConfig = async (config) => {
    try {
      await api.put('/correlation/policy-config', config);
      toast.success('Policy configuration saved');
      setPolicyConfig({ config, is_custom: true });
      setShowPolicySettings(false);
    } catch (error) {
      toast.error('Failed to save policy configuration');
    }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    fetchTickets();
  }, [ticketStatusFilter, ticketPriorityFilter]);

  const filteredTickets = tickets.filter(t => {
    if (!searchTerm) return true;
    return t.title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
           t.description?.toLowerCase().includes(searchTerm.toLowerCase());
  });

  const priorityData = ticketStats ? [
    { name: 'Critical', value: ticketStats.by_priority.critical, color: COLORS.critical },
    { name: 'High', value: ticketStats.by_priority.high, color: COLORS.high },
    { name: 'Medium', value: ticketStats.by_priority.medium, color: COLORS.medium },
    { name: 'Low', value: ticketStats.by_priority.low, color: COLORS.low }
  ] : [];

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className={`p-2 rounded-xl ${isDarkMode ? 'bg-violet-500/20' : 'bg-violet-100'}`}>
            <Link2 className="w-6 h-6 text-violet-500" />
          </div>
          <div>
            <h1 className={`text-2xl font-bold ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>
              Correlation Engine
            </h1>
            <p className={`text-sm ${isDarkMode ? 'text-zinc-400' : 'text-slate-500'}`}>
              Threat-Vulnerability Correlation & Risk-Based Prioritization
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button 
            variant="outline" 
            size="sm"
            onClick={() => setShowPolicySettings(true)}
            data-testid="policy-settings-btn"
          >
            <Settings className="w-4 h-4 mr-2" />
            Policy Settings
          </Button>
          <Button 
            onClick={handleBulkCalculateRisk}
            disabled={calculating}
            data-testid="calculate-risk-btn"
          >
            {calculating ? (
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <Zap className="w-4 h-4 mr-2" />
            )}
            Calculate Risk Scores
          </Button>
        </div>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className={isDarkMode ? 'bg-zinc-800' : 'bg-slate-100'}>
          <TabsTrigger value="dashboard" data-testid="dashboard-tab">
            <BarChart3 className="w-4 h-4 mr-2" />
            Dashboard
          </TabsTrigger>
          <TabsTrigger value="tickets" data-testid="tickets-tab">
            <Ticket className="w-4 h-4 mr-2" />
            Remediation Tickets
          </TabsTrigger>
          <TabsTrigger value="policies" data-testid="policies-tab">
            <Target className="w-4 h-4 mr-2" />
            Automation Policies
          </TabsTrigger>
        </TabsList>

        {/* Dashboard Tab */}
        <TabsContent value="dashboard" className="space-y-6">
          {dashboard && (
            <>
              {/* Risk Metrics Row */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
                <MetricCard
                  title="Avg Risk Score"
                  value={dashboard.risk_metrics.average_risk_score.toFixed(1)}
                  subtitle="Across all vulnerabilities"
                  icon={<TrendingUp className="w-5 h-5" />}
                  color="violet"
                  isDarkMode={isDarkMode}
                />
                <MetricCard
                  title="Critical Risk"
                  value={dashboard.risk_metrics.critical_risk_count}
                  subtitle="Score ≥ 80"
                  icon={<AlertTriangle className="w-5 h-5" />}
                  color="red"
                  isDarkMode={isDarkMode}
                />
                <MetricCard
                  title="High Risk"
                  value={dashboard.risk_metrics.high_risk_count}
                  subtitle="Score 60-79"
                  icon={<Activity className="w-5 h-5" />}
                  color="orange"
                  isDarkMode={isDarkMode}
                />
                <MetricCard
                  title="KEV Vulnerabilities"
                  value={dashboard.risk_metrics.kev_vulnerabilities}
                  subtitle="In CISA catalog"
                  icon={<Shield className="w-5 h-5" />}
                  color="rose"
                  isDarkMode={isDarkMode}
                />
                <MetricCard
                  title="Total Assessed"
                  value={dashboard.risk_metrics.total_assessed}
                  subtitle="With risk scores"
                  icon={<CheckCircle className="w-5 h-5" />}
                  color="green"
                  isDarkMode={isDarkMode}
                />
              </div>

              {/* Correlation & Ticket Metrics */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Correlation Metrics */}
                <div className={`rounded-xl p-6 ${isDarkMode ? 'bg-zinc-900/50 border border-white/10' : 'bg-white border border-slate-200 shadow-sm'}`}>
                  <h3 className={`text-lg font-semibold mb-4 ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>
                    Threat Correlation
                  </h3>
                  <div className="grid grid-cols-2 gap-4">
                    <div className={`p-4 rounded-lg ${isDarkMode ? 'bg-zinc-800' : 'bg-slate-50'}`}>
                      <div className={`text-2xl font-bold ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>
                        {dashboard.correlation_metrics.correlated_vulnerabilities}
                      </div>
                      <div className={`text-sm ${isDarkMode ? 'text-zinc-400' : 'text-slate-500'}`}>
                        Correlated Vulns
                      </div>
                    </div>
                    <div className={`p-4 rounded-lg ${isDarkMode ? 'bg-zinc-800' : 'bg-slate-50'}`}>
                      <div className={`text-2xl font-bold ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>
                        {dashboard.correlation_metrics.active_threats}
                      </div>
                      <div className={`text-sm ${isDarkMode ? 'text-zinc-400' : 'text-slate-500'}`}>
                        Active Threats
                      </div>
                    </div>
                    <div className={`p-4 rounded-lg ${isDarkMode ? 'bg-zinc-800' : 'bg-slate-50'}`}>
                      <div className={`text-2xl font-bold text-red-500`}>
                        {dashboard.correlation_metrics.weaponized_threats}
                      </div>
                      <div className={`text-sm ${isDarkMode ? 'text-zinc-400' : 'text-slate-500'}`}>
                        Weaponized
                      </div>
                    </div>
                    <div className={`p-4 rounded-lg ${isDarkMode ? 'bg-zinc-800' : 'bg-slate-50'}`}>
                      <div className={`text-2xl font-bold text-violet-500`}>
                        {dashboard.correlation_metrics.threat_coverage}%
                      </div>
                      <div className={`text-sm ${isDarkMode ? 'text-zinc-400' : 'text-slate-500'}`}>
                        Threat Coverage
                      </div>
                    </div>
                  </div>
                </div>

                {/* Ticket Metrics */}
                <div className={`rounded-xl p-6 ${isDarkMode ? 'bg-zinc-900/50 border border-white/10' : 'bg-white border border-slate-200 shadow-sm'}`}>
                  <h3 className={`text-lg font-semibold mb-4 ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>
                    Remediation Tickets
                  </h3>
                  <div className="grid grid-cols-2 gap-4">
                    <div className={`p-4 rounded-lg ${isDarkMode ? 'bg-zinc-800' : 'bg-slate-50'}`}>
                      <div className={`text-2xl font-bold ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>
                        {dashboard.ticket_metrics.total_tickets}
                      </div>
                      <div className={`text-sm ${isDarkMode ? 'text-zinc-400' : 'text-slate-500'}`}>
                        Total Tickets
                      </div>
                    </div>
                    <div className={`p-4 rounded-lg ${isDarkMode ? 'bg-zinc-800' : 'bg-slate-50'}`}>
                      <div className={`text-2xl font-bold text-orange-500`}>
                        {dashboard.ticket_metrics.open_tickets}
                      </div>
                      <div className={`text-sm ${isDarkMode ? 'text-zinc-400' : 'text-slate-500'}`}>
                        Open
                      </div>
                    </div>
                    <div className={`p-4 rounded-lg ${isDarkMode ? 'bg-zinc-800' : 'bg-slate-50'}`}>
                      <div className={`text-2xl font-bold text-violet-500`}>
                        {dashboard.ticket_metrics.auto_generated}
                      </div>
                      <div className={`text-sm ${isDarkMode ? 'text-zinc-400' : 'text-slate-500'}`}>
                        Auto-Generated
                      </div>
                    </div>
                    <div className={`p-4 rounded-lg ${isDarkMode ? 'bg-zinc-800' : 'bg-slate-50'}`}>
                      <div className={`text-2xl font-bold ${dashboard.ticket_metrics.overdue > 0 ? 'text-red-500' : 'text-green-500'}`}>
                        {dashboard.ticket_metrics.sla_compliance}%
                      </div>
                      <div className={`text-sm ${isDarkMode ? 'text-zinc-400' : 'text-slate-500'}`}>
                        SLA Compliance
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Policy Configuration Display */}
              <div className={`rounded-xl p-6 ${isDarkMode ? 'bg-zinc-900/50 border border-white/10' : 'bg-white border border-slate-200 shadow-sm'}`}>
                <div className="flex items-center justify-between mb-4">
                  <h3 className={`text-lg font-semibold ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>
                    Active Policy Triggers
                  </h3>
                  <Badge variant={policyConfig?.is_custom ? "default" : "outline"}>
                    {policyConfig?.is_custom ? "Custom" : "Default"}
                  </Badge>
                </div>
                <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                  <PolicyBadge label="Critical Threshold" value={`≥ ${dashboard.policy_config.critical_risk_score}`} isDarkMode={isDarkMode} />
                  <PolicyBadge label="High Threshold" value={`≥ ${dashboard.policy_config.high_risk_score}`} isDarkMode={isDarkMode} />
                  <PolicyBadge label="KEV Auto-Ticket" value={dashboard.policy_config.kev_presence ? "Enabled" : "Disabled"} isDarkMode={isDarkMode} />
                  <PolicyBadge label="EPSS Threshold" value={`≥ ${(dashboard.policy_config.epss_threshold * 100).toFixed(0)}%`} isDarkMode={isDarkMode} />
                  <PolicyBadge label="Weaponized Scan" value={dashboard.policy_config.weaponized_threat ? "Enabled" : "Disabled"} isDarkMode={isDarkMode} />
                </div>
              </div>
            </>
          )}
        </TabsContent>

        {/* Tickets Tab */}
        <TabsContent value="tickets" className="space-y-4">
          {/* Filters */}
          <div className="flex items-center justify-between flex-wrap gap-4">
            <div className="flex items-center gap-2">
              <div className="relative">
                <Search className={`absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 ${isDarkMode ? 'text-zinc-400' : 'text-slate-400'}`} />
                <Input
                  placeholder="Search tickets..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className={`pl-10 w-64 ${isDarkMode ? 'bg-zinc-900 border-white/10' : ''}`}
                  data-testid="ticket-search"
                />
              </div>
              <Select value={ticketStatusFilter} onValueChange={setTicketStatusFilter}>
                <SelectTrigger className={`w-32 ${isDarkMode ? 'bg-zinc-900 border-white/10' : ''}`}>
                  <SelectValue placeholder="Status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Status</SelectItem>
                  <SelectItem value="open">Open</SelectItem>
                  <SelectItem value="in_progress">In Progress</SelectItem>
                  <SelectItem value="resolved">Resolved</SelectItem>
                </SelectContent>
              </Select>
              <Select value={ticketPriorityFilter} onValueChange={setTicketPriorityFilter}>
                <SelectTrigger className={`w-32 ${isDarkMode ? 'bg-zinc-900 border-white/10' : ''}`}>
                  <SelectValue placeholder="Priority" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Priority</SelectItem>
                  <SelectItem value="critical">Critical</SelectItem>
                  <SelectItem value="high">High</SelectItem>
                  <SelectItem value="medium">Medium</SelectItem>
                  <SelectItem value="low">Low</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <Button onClick={() => setShowCreateTicket(true)} data-testid="create-ticket-btn">
              <Plus className="w-4 h-4 mr-2" />
              Create Ticket
            </Button>
          </div>

          {/* Stats Bar */}
          {ticketStats && (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <StatBadge label="Total" value={ticketStats.total} color="violet" isDarkMode={isDarkMode} />
              <StatBadge label="Open" value={ticketStats.open} color="orange" isDarkMode={isDarkMode} />
              <StatBadge label="In Progress" value={ticketStats.in_progress} color="blue" isDarkMode={isDarkMode} />
              <StatBadge label="Resolved" value={ticketStats.resolved} color="green" isDarkMode={isDarkMode} />
            </div>
          )}

          {/* Tickets List */}
          <div className="space-y-3">
            {loading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="w-8 h-8 animate-spin text-violet-500" />
              </div>
            ) : filteredTickets.length === 0 ? (
              <div className={`text-center py-12 ${isDarkMode ? 'text-zinc-400' : 'text-slate-500'}`}>
                <Ticket className="w-12 h-12 mx-auto mb-4 opacity-50" />
                <p>No tickets found</p>
              </div>
            ) : (
              filteredTickets.map(ticket => (
                <TicketCard
                  key={ticket.id}
                  ticket={ticket}
                  isDarkMode={isDarkMode}
                  onStatusChange={handleUpdateTicketStatus}
                  onDelete={handleDeleteTicket}
                  onView={() => setShowTicketDetail(ticket)}
                />
              ))
            )}
          </div>
        </TabsContent>

        {/* Policies Tab */}
        <TabsContent value="policies" className="space-y-6">
          <div className={`rounded-xl p-6 ${isDarkMode ? 'bg-zinc-900/50 border border-white/10' : 'bg-white border border-slate-200 shadow-sm'}`}>
            <h3 className={`text-lg font-semibold mb-4 ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>
              Automation Policy Rules
            </h3>
            <p className={`mb-6 ${isDarkMode ? 'text-zinc-400' : 'text-slate-500'}`}>
              Configure automatic ticketing and scanning policies based on risk thresholds.
            </p>
            
            {policyConfig && (
              <div className="space-y-6">
                <PolicyRule
                  title="Critical Risk Auto-Ticket"
                  description={`Automatically create critical priority tickets for vulnerabilities with risk score ≥ ${policyConfig.config.critical_risk_score}`}
                  enabled={true}
                  isDarkMode={isDarkMode}
                />
                <PolicyRule
                  title="High Risk Auto-Ticket"
                  description={`Automatically create high priority tickets for vulnerabilities with risk score ≥ ${policyConfig.config.high_risk_score}`}
                  enabled={true}
                  isDarkMode={isDarkMode}
                />
                <PolicyRule
                  title="CISA KEV Auto-Ticket"
                  description="Automatically create critical tickets for vulnerabilities in the CISA Known Exploited Vulnerabilities catalog"
                  enabled={policyConfig.config.kev_presence}
                  isDarkMode={isDarkMode}
                />
                <PolicyRule
                  title="High EPSS Auto-Ticket"
                  description={`Automatically ticket vulnerabilities with EPSS probability ≥ ${(policyConfig.config.epss_threshold * 100).toFixed(0)}%`}
                  enabled={true}
                  isDarkMode={isDarkMode}
                />
                <PolicyRule
                  title="Weaponized Threat Scan"
                  description="Trigger automated scans when a weaponized exploit is detected in threat intelligence"
                  enabled={policyConfig.config.weaponized_threat}
                  isDarkMode={isDarkMode}
                />
              </div>
            )}
            
            <div className="mt-6 pt-6 border-t border-white/10">
              <Button onClick={() => setShowPolicySettings(true)} data-testid="edit-policies-btn">
                <Settings className="w-4 h-4 mr-2" />
                Edit Policy Configuration
              </Button>
            </div>
          </div>
        </TabsContent>
      </Tabs>

      {/* Create Ticket Dialog */}
      <Dialog open={showCreateTicket} onOpenChange={setShowCreateTicket}>
        <DialogContent className={isDarkMode ? 'bg-zinc-900 border-white/10' : ''}>
          <DialogHeader>
            <DialogTitle className={isDarkMode ? 'text-white' : ''}>Create Remediation Ticket</DialogTitle>
            <DialogDescription>Create a new ticket for tracking vulnerability remediation.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label className={isDarkMode ? 'text-zinc-300' : ''}>Title</Label>
              <Input
                value={newTicket.title}
                onChange={(e) => setNewTicket({...newTicket, title: e.target.value})}
                placeholder="Brief description of the issue"
                className={isDarkMode ? 'bg-zinc-800 border-white/10' : ''}
                data-testid="ticket-title-input"
              />
            </div>
            <div>
              <Label className={isDarkMode ? 'text-zinc-300' : ''}>Description</Label>
              <Textarea
                value={newTicket.description}
                onChange={(e) => setNewTicket({...newTicket, description: e.target.value})}
                placeholder="Detailed description and context"
                rows={4}
                className={isDarkMode ? 'bg-zinc-800 border-white/10' : ''}
                data-testid="ticket-desc-input"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className={isDarkMode ? 'text-zinc-300' : ''}>Priority</Label>
                <Select value={newTicket.priority} onValueChange={(v) => setNewTicket({...newTicket, priority: v})}>
                  <SelectTrigger className={isDarkMode ? 'bg-zinc-800 border-white/10' : ''}>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="critical">Critical</SelectItem>
                    <SelectItem value="high">High</SelectItem>
                    <SelectItem value="medium">Medium</SelectItem>
                    <SelectItem value="low">Low</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className={isDarkMode ? 'text-zinc-300' : ''}>Assigned Team</Label>
                <Select value={newTicket.assigned_team} onValueChange={(v) => setNewTicket({...newTicket, assigned_team: v})}>
                  <SelectTrigger className={isDarkMode ? 'bg-zinc-800 border-white/10' : ''}>
                    <SelectValue placeholder="Select team" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="security">Security</SelectItem>
                    <SelectItem value="secops">SecOps</SelectItem>
                    <SelectItem value="dev">Development</SelectItem>
                    <SelectItem value="it">IT</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                id="sync-external"
                checked={newTicket.sync_to_external}
                onChange={(e) => setNewTicket({...newTicket, sync_to_external: e.target.checked})}
                className="rounded"
              />
              <Label htmlFor="sync-external" className={`cursor-pointer ${isDarkMode ? 'text-zinc-300' : ''}`}>
                Also create in external ITSM (Jira/ServiceNow)
              </Label>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreateTicket(false)}>Cancel</Button>
            <Button onClick={handleCreateTicket} data-testid="submit-ticket-btn">Create Ticket</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Policy Settings Dialog */}
      <Dialog open={showPolicySettings} onOpenChange={setShowPolicySettings}>
        <DialogContent className={isDarkMode ? 'bg-zinc-900 border-white/10' : ''}>
          <DialogHeader>
            <DialogTitle className={isDarkMode ? 'text-white' : ''}>Policy Configuration</DialogTitle>
            <DialogDescription>Configure automated ticketing and scanning thresholds.</DialogDescription>
          </DialogHeader>
          {policyConfig && (
            <PolicySettingsForm
              config={policyConfig.config}
              isDarkMode={isDarkMode}
              onSave={handleSavePolicyConfig}
              onCancel={() => setShowPolicySettings(false)}
            />
          )}
        </DialogContent>
      </Dialog>

      {/* Ticket Detail Dialog */}
      <Dialog open={!!showTicketDetail} onOpenChange={() => setShowTicketDetail(null)}>
        <DialogContent className={`max-w-2xl ${isDarkMode ? 'bg-zinc-900 border-white/10' : ''}`}>
          {showTicketDetail && (
            <>
              <DialogHeader>
                <DialogTitle className={isDarkMode ? 'text-white' : ''}>
                  {showTicketDetail.title}
                </DialogTitle>
              </DialogHeader>
              <div className="space-y-4 py-4">
                <div className="flex gap-2">
                  <Badge className={`${
                    showTicketDetail.priority === 'critical' ? 'bg-red-500' :
                    showTicketDetail.priority === 'high' ? 'bg-orange-500' :
                    showTicketDetail.priority === 'medium' ? 'bg-yellow-500' : 'bg-green-500'
                  } text-white`}>
                    {showTicketDetail.priority}
                  </Badge>
                  <Badge variant="outline">{showTicketDetail.status}</Badge>
                  {showTicketDetail.auto_generated && <Badge variant="secondary">Auto-Generated</Badge>}
                </div>
                <div className={`p-4 rounded-lg ${isDarkMode ? 'bg-zinc-800' : 'bg-slate-50'}`}>
                  <pre className={`whitespace-pre-wrap text-sm ${isDarkMode ? 'text-zinc-300' : 'text-slate-600'}`}>
                    {showTicketDetail.description}
                  </pre>
                </div>
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <span className={isDarkMode ? 'text-zinc-400' : 'text-slate-500'}>Created:</span>
                    <span className={`ml-2 ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>
                      {new Date(showTicketDetail.created_at).toLocaleString()}
                    </span>
                  </div>
                  <div>
                    <span className={isDarkMode ? 'text-zinc-400' : 'text-slate-500'}>Due:</span>
                    <span className={`ml-2 ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>
                      {new Date(showTicketDetail.due_date).toLocaleString()}
                    </span>
                  </div>
                  {showTicketDetail.assigned_team && (
                    <div>
                      <span className={isDarkMode ? 'text-zinc-400' : 'text-slate-500'}>Team:</span>
                      <span className={`ml-2 ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>
                        {showTicketDetail.assigned_team}
                      </span>
                    </div>
                  )}
                  {showTicketDetail.risk_score && (
                    <div>
                      <span className={isDarkMode ? 'text-zinc-400' : 'text-slate-500'}>Risk Score:</span>
                      <span className={`ml-2 ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>
                        {showTicketDetail.risk_score}
                      </span>
                    </div>
                  )}
                </div>
                {showTicketDetail.external_ticket_id && (
                  <div className={`flex items-center gap-2 p-3 rounded-lg ${isDarkMode ? 'bg-violet-500/10' : 'bg-violet-50'}`}>
                    <ExternalLink className="w-4 h-4 text-violet-500" />
                    <span className={isDarkMode ? 'text-zinc-300' : 'text-slate-600'}>
                      External: {showTicketDetail.external_system} - {showTicketDetail.external_ticket_id}
                    </span>
                  </div>
                )}
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

// Sub-components
function MetricCard({ title, value, subtitle, icon, color, isDarkMode }) {
  const colorClasses = {
    violet: 'text-violet-500 bg-violet-500/10',
    red: 'text-red-500 bg-red-500/10',
    orange: 'text-orange-500 bg-orange-500/10',
    rose: 'text-rose-500 bg-rose-500/10',
    green: 'text-green-500 bg-green-500/10',
    blue: 'text-blue-500 bg-blue-500/10'
  };

  return (
    <div className={`rounded-xl p-4 ${isDarkMode ? 'bg-zinc-900/50 border border-white/10' : 'bg-white border border-slate-200 shadow-sm'}`}>
      <div className="flex items-center justify-between mb-2">
        <span className={`text-sm ${isDarkMode ? 'text-zinc-400' : 'text-slate-500'}`}>{title}</span>
        <div className={`p-1.5 rounded-lg ${colorClasses[color]}`}>
          {icon}
        </div>
      </div>
      <div className={`text-2xl font-bold ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>{value}</div>
      <div className={`text-xs ${isDarkMode ? 'text-zinc-500' : 'text-slate-400'}`}>{subtitle}</div>
    </div>
  );
}

function PolicyBadge({ label, value, isDarkMode }) {
  return (
    <div className={`p-3 rounded-lg text-center ${isDarkMode ? 'bg-zinc-800' : 'bg-slate-50'}`}>
      <div className={`text-xs ${isDarkMode ? 'text-zinc-400' : 'text-slate-500'}`}>{label}</div>
      <div className={`text-sm font-semibold ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>{value}</div>
    </div>
  );
}

function StatBadge({ label, value, color, isDarkMode }) {
  const colorClasses = {
    violet: 'border-violet-500/30 bg-violet-500/10',
    orange: 'border-orange-500/30 bg-orange-500/10',
    blue: 'border-blue-500/30 bg-blue-500/10',
    green: 'border-green-500/30 bg-green-500/10'
  };
  
  return (
    <div className={`p-3 rounded-lg border ${colorClasses[color]}`}>
      <div className={`text-xl font-bold ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>{value}</div>
      <div className={`text-sm ${isDarkMode ? 'text-zinc-400' : 'text-slate-500'}`}>{label}</div>
    </div>
  );
}

function TicketCard({ ticket, isDarkMode, onStatusChange, onDelete, onView }) {
  const priorityColors = {
    critical: 'bg-red-500',
    high: 'bg-orange-500',
    medium: 'bg-yellow-500',
    low: 'bg-green-500'
  };
  
  const statusIcons = {
    open: <Clock className="w-4 h-4 text-orange-500" />,
    in_progress: <RefreshCw className="w-4 h-4 text-blue-500" />,
    resolved: <CheckCircle className="w-4 h-4 text-green-500" />,
    closed: <XCircle className="w-4 h-4 text-zinc-500" />
  };

  return (
    <div className={`p-4 rounded-xl ${isDarkMode ? 'bg-zinc-900/50 border border-white/10 hover:border-white/20' : 'bg-white border border-slate-200 shadow-sm hover:shadow'} transition-all`}
         data-testid={`ticket-card-${ticket.id}`}>
      <div className="flex items-start justify-between">
        <div className="flex-1">
          <div className="flex items-center gap-2 mb-1">
            <Badge className={`${priorityColors[ticket.priority]} text-white text-xs`}>
              {ticket.priority}
            </Badge>
            <div className="flex items-center gap-1">
              {statusIcons[ticket.status]}
              <span className={`text-xs ${isDarkMode ? 'text-zinc-400' : 'text-slate-500'}`}>
                {ticket.status.replace('_', ' ')}
              </span>
            </div>
            {ticket.auto_generated && (
              <Badge variant="outline" className="text-xs">
                <Zap className="w-3 h-3 mr-1" />
                Auto
              </Badge>
            )}
          </div>
          <h4 className={`font-medium ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>
            {ticket.title}
          </h4>
          <p className={`text-sm mt-1 line-clamp-2 ${isDarkMode ? 'text-zinc-400' : 'text-slate-500'}`}>
            {ticket.description?.split('\n')[0]}
          </p>
          <div className={`flex items-center gap-4 mt-2 text-xs ${isDarkMode ? 'text-zinc-500' : 'text-slate-400'}`}>
            <span className="flex items-center gap-1">
              <Calendar className="w-3 h-3" />
              Due: {new Date(ticket.due_date).toLocaleDateString()}
            </span>
            {ticket.assigned_team && (
              <span className="flex items-center gap-1">
                <Users className="w-3 h-3" />
                {ticket.assigned_team}
              </span>
            )}
          </div>
        </div>
        <div className="flex items-center gap-1">
          <Button variant="ghost" size="sm" onClick={onView} data-testid={`view-ticket-${ticket.id}`}>
            <Eye className="w-4 h-4" />
          </Button>
          {ticket.status === 'open' && (
            <Button variant="ghost" size="sm" onClick={() => onStatusChange(ticket.id, 'in_progress')}>
              <Play className="w-4 h-4" />
            </Button>
          )}
          {ticket.status === 'in_progress' && (
            <Button variant="ghost" size="sm" onClick={() => onStatusChange(ticket.id, 'resolved')}>
              <CheckCircle className="w-4 h-4" />
            </Button>
          )}
          {ticket.status !== 'resolved' && ticket.status !== 'closed' && (
            <Button variant="ghost" size="sm" onClick={() => onDelete(ticket.id)} className="text-red-500 hover:text-red-600">
              <Trash2 className="w-4 h-4" />
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}

function PolicyRule({ title, description, enabled, isDarkMode }) {
  return (
    <div className={`flex items-start justify-between p-4 rounded-lg ${isDarkMode ? 'bg-zinc-800' : 'bg-slate-50'}`}>
      <div className="flex-1">
        <h4 className={`font-medium ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>{title}</h4>
        <p className={`text-sm mt-1 ${isDarkMode ? 'text-zinc-400' : 'text-slate-500'}`}>{description}</p>
      </div>
      <Badge variant={enabled ? "default" : "secondary"} className={enabled ? 'bg-green-500' : ''}>
        {enabled ? 'Active' : 'Inactive'}
      </Badge>
    </div>
  );
}

function PolicySettingsForm({ config, isDarkMode, onSave, onCancel }) {
  const [formData, setFormData] = useState({...config});

  return (
    <div className="space-y-4 py-4">
      <div>
        <Label className={isDarkMode ? 'text-zinc-300' : ''}>Critical Risk Score Threshold</Label>
        <Input
          type="number"
          value={formData.critical_risk_score}
          onChange={(e) => setFormData({...formData, critical_risk_score: parseFloat(e.target.value)})}
          className={isDarkMode ? 'bg-zinc-800 border-white/10' : ''}
        />
        <p className={`text-xs mt-1 ${isDarkMode ? 'text-zinc-500' : 'text-slate-400'}`}>
          Auto-create critical tickets when risk score exceeds this value
        </p>
      </div>
      <div>
        <Label className={isDarkMode ? 'text-zinc-300' : ''}>High Risk Score Threshold</Label>
        <Input
          type="number"
          value={formData.high_risk_score}
          onChange={(e) => setFormData({...formData, high_risk_score: parseFloat(e.target.value)})}
          className={isDarkMode ? 'bg-zinc-800 border-white/10' : ''}
        />
      </div>
      <div>
        <Label className={isDarkMode ? 'text-zinc-300' : ''}>EPSS Threshold (%)</Label>
        <Input
          type="number"
          min="0"
          max="100"
          value={formData.epss_threshold * 100}
          onChange={(e) => setFormData({...formData, epss_threshold: parseFloat(e.target.value) / 100})}
          className={isDarkMode ? 'bg-zinc-800 border-white/10' : ''}
        />
      </div>
      <div className="flex items-center gap-2">
        <input
          type="checkbox"
          id="kev-auto"
          checked={formData.kev_presence}
          onChange={(e) => setFormData({...formData, kev_presence: e.target.checked})}
          className="rounded"
        />
        <Label htmlFor="kev-auto" className={`cursor-pointer ${isDarkMode ? 'text-zinc-300' : ''}`}>
          Auto-ticket CISA KEV vulnerabilities
        </Label>
      </div>
      <div className="flex items-center gap-2">
        <input
          type="checkbox"
          id="weaponized-scan"
          checked={formData.weaponized_threat}
          onChange={(e) => setFormData({...formData, weaponized_threat: e.target.checked})}
          className="rounded"
        />
        <Label htmlFor="weaponized-scan" className={`cursor-pointer ${isDarkMode ? 'text-zinc-300' : ''}`}>
          Auto-scan on weaponized threat detection
        </Label>
      </div>
      <DialogFooter>
        <Button variant="outline" onClick={onCancel}>Cancel</Button>
        <Button onClick={() => onSave(formData)} data-testid="save-policy-btn">Save Configuration</Button>
      </DialogFooter>
    </div>
  );
}
